import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Building2, User, MapPin, ArrowRight, ArrowLeft, CheckCircle, Shield, Plane } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_be106047-7bc1-4755-b814-a58a16500b58/artifacts/p2la4lzy_ChatGPT%20Image%20Dec%2028%2C%202025%2C%2009_16_43%20PM.png";

const AgentRegisterPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [countryCode, setCountryCode] = useState("+91");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company_name: "",
    phone: "",
    is_gst_registered: true,
    gst_number: "",
    pan_number: "",
    address: "",
    city: "",
    state: ""
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateStep1 = () => {
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error("Please fill all required fields");
      return false;
    }
    if (!formData.email.includes("@")) {
      toast.error("Please enter a valid email");
      return false;
    }
    if (!/^\d{10}$/.test(formData.phone)) {
      toast.error("Mobile number must be exactly 10 digits");
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.company_name || !formData.address || !formData.city || !formData.state) {
      toast.error("Please fill all required fields");
      return false;
    }
    return true;
  };

  const nextStep = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/agent-register`, {
        name: formData.name,
        email: (formData.email || "").trim().toLowerCase(),
        company_name: formData.company_name,
        phone: `${countryCode}${formData.phone}`,
        is_gst_registered: formData.is_gst_registered,
        gst_number: formData.is_gst_registered ? (formData.gst_number || null) : null,
        pan_number: formData.pan_number || null,
        address: formData.address,
        city: formData.city,
        state: formData.state
      });
      toast.success("Registration submitted!");
      setStep(4);
    } catch (error) {
      const detail = error.response?.data?.detail || "Registration failed. Please try again.";
      const isDuplicate = error.response?.status === 409 || /already registered/i.test(detail);
      toast.error(detail, { duration: isDuplicate ? 8000 : 4000 });
      if (isDuplicate) setStep(1); // jump back so user can edit the email field
    } finally {
      setLoading(false);
    }
  };

  const stepTitles = ["Personal Info", "Business Info", "Review"];

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-amber-50" data-testid="agent-register-page">
      <div className="min-h-screen flex">
        {/* Left Panel - Branding */}
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-emerald-600 to-emerald-800 p-12 flex-col justify-between relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-10 w-64 h-64 border border-white/30 rounded-full" />
            <div className="absolute bottom-20 right-10 w-96 h-96 border border-white/20 rounded-full" />
          </div>
          
          <div className="relative z-10">
            <Link to="/" className="flex items-center gap-3 mb-8">
              <img src={LOGO_URL} alt="Logo" className="h-12 w-12 rounded-lg" />
              <div>
                <h1 className="text-white text-xl font-bold">Happy Holidays DMC</h1>
                <p className="text-emerald-200 text-sm">B2B Travel Platform</p>
              </div>
            </Link>
          </div>

          <div className="relative z-10 space-y-6">
            <h2 className="text-4xl font-bold text-white leading-tight">
              Partner With Us<br />& Grow Your<br />Travel Business
            </h2>
            <p className="text-emerald-100 text-lg max-w-md">
              Access premium travel packages, competitive B2B pricing, and dedicated support for your agency.
            </p>
            
            <div className="space-y-4 pt-4">
              {["100+ Curated Packages", "24/7 Dedicated Support", "Quick Quote Turnaround", "Marketing Support"].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-300" />
                  <span className="text-white">{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="relative z-10">
            <p className="text-emerald-200 text-sm">
              Already have an account? <Link to="/login" className="text-white font-medium hover:underline">Sign In</Link>
            </p>
          </div>
        </div>

        {/* Right Panel - Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12">
          <div className="w-full max-w-lg">
            {/* Mobile Header */}
            <div className="lg:hidden flex items-center justify-between mb-6">
              <Link to="/" className="flex items-center gap-2">
                <img src={LOGO_URL} alt="Logo" className="h-8 w-8 rounded-lg" />
                <span className="font-bold text-gray-900">Happy Holidays</span>
              </Link>
              <Link to="/login" className="text-sm text-emerald-600 font-medium">Sign In</Link>
            </div>

            {step === 4 ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 text-center"
              >
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-emerald-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">Registration Submitted!</h2>
                <p className="text-gray-500 mb-8">
                  Our team will review your application and send approval within 24-48 hours.
                </p>
                <div className="p-4 bg-emerald-50 rounded-xl mb-6">
                  <Shield className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">
                    Once approved, login using OTP sent to <span className="font-medium">{formData.email}</span>
                  </p>
                </div>
                <div className="space-y-3">
                  <Link to="/" className="block">
                    <Button className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 rounded-xl">Back to Home</Button>
                  </Link>
                  <Link to="/login" className="block">
                    <Button variant="outline" className="w-full h-11 border-gray-200 rounded-xl">Go to Login</Button>
                  </Link>
                </div>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8"
              >
                <div className="text-center mb-6">
                  <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Plane className="w-7 h-7 text-emerald-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">Become an Agent</h2>
                  <p className="text-gray-500 text-sm mt-1">Create your partner account</p>
                </div>

                {/* Progress Steps */}
                <div className="flex items-center justify-between mb-8 px-4">
                  {[1, 2, 3].map((s, i) => (
                    <div key={s} className="flex items-center">
                      <div className="flex flex-col items-center">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                          step >= s 
                            ? "bg-emerald-600 text-white" 
                            : "bg-gray-100 text-gray-400"
                        }`}>
                          {step > s ? <CheckCircle className="w-5 h-5" /> : s}
                        </div>
                        <span className={`text-xs mt-1 ${step >= s ? "text-emerald-600" : "text-gray-400"}`}>
                          {stepTitles[i]}
                        </span>
                      </div>
                      {s < 3 && (
                        <div className={`w-12 sm:w-20 h-1 mx-2 rounded-full transition-all ${
                          step > s ? "bg-emerald-600" : "bg-gray-100"
                        }`} />
                      )}
                    </div>
                  ))}
                </div>

                <form onSubmit={handleSubmit} data-testid="register-form">
                  {/* Step 1: Personal Info */}
                  {step === 1 && (
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="space-y-5"
                    >
                      <div className="flex items-center gap-2 text-emerald-600 mb-4">
                        <User className="w-5 h-5" />
                        <span className="font-medium">Personal Information</span>
                      </div>

                      <div>
                        <Label className="text-gray-700">Full Name *</Label>
                        <Input 
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          className="mt-1.5 h-11 border-gray-200 focus:border-emerald-500"
                          placeholder="Your full name"
                          data-testid="register-name"
                        />
                      </div>

                      <div>
                        <Label className="text-gray-700">Email Address *</Label>
                        <Input 
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="mt-1.5 h-11 border-gray-200 focus:border-emerald-500"
                          placeholder="you@company.com"
                          data-testid="register-email"
                        />
                        <p className="text-xs text-gray-400 mt-1">Used for OTP login</p>
                      </div>

                      <div>
                        <Label className="text-gray-700">Mobile Number *</Label>
                        <div className="flex gap-2 mt-1.5">
                          <select
                            value={countryCode}
                            onChange={e => setCountryCode(e.target.value)}
                            className="h-11 px-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:border-emerald-500 w-24 shrink-0"
                            data-testid="register-country-code"
                          >
                            <option value="+91">+91 IN</option>
                            <option value="+1">+1 US</option>
                            <option value="+44">+44 UK</option>
                            <option value="+971">+971 UAE</option>
                            <option value="+65">+65 SG</option>
                            <option value="+61">+61 AU</option>
                            <option value="+60">+60 MY</option>
                            <option value="+66">+66 TH</option>
                            <option value="+94">+94 LK</option>
                            <option value="+977">+977 NP</option>
                          </select>
                          <Input
                            name="phone"
                            type="tel"
                            inputMode="numeric"
                            pattern="[0-9]{10}"
                            maxLength={10}
                            value={formData.phone}
                            onChange={e => {
                              const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                              setFormData(prev => ({ ...prev, phone: val }));
                            }}
                            className="h-11 border-gray-200 focus:border-emerald-500 flex-1"
                            placeholder="10-digit mobile number"
                            data-testid="register-phone"
                          />
                        </div>
                        {formData.phone && formData.phone.length !== 10 && (
                          <p className="text-xs text-red-500 mt-1">Mobile number must be exactly 10 digits ({formData.phone.length}/10)</p>
                        )}
                      </div>

                      <Button 
                        type="button" 
                        onClick={nextStep}
                        className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 rounded-xl mt-2"
                      >
                        Continue <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </motion.div>
                  )}

                  {/* Step 2: Business Info */}
                  {step === 2 && (
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="space-y-5"
                    >
                      <div className="flex items-center gap-2 text-emerald-600 mb-4">
                        <Building2 className="w-5 h-5" />
                        <span className="font-medium">Business Information</span>
                      </div>

                      <div>
                        <Label className="text-gray-700">Company/Agency Name *</Label>
                        <Input 
                          name="company_name"
                          value={formData.company_name}
                          onChange={handleChange}
                          className="mt-1.5 h-11 border-gray-200 focus:border-emerald-500"
                          placeholder="Your company name"
                          data-testid="register-company"
                        />
                      </div>

                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div>
                          <Label className="text-gray-700 font-medium">GST Registered</Label>
                          <p className="text-[11px] text-gray-500">Turn ON if your company has a valid GSTIN</p>
                        </div>
                        <Switch
                          checked={formData.is_gst_registered}
                          onCheckedChange={v => setFormData({ ...formData, is_gst_registered: v, gst_number: v ? formData.gst_number : "" })}
                          data-testid="register-gst-toggle"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        {formData.is_gst_registered && (
                          <div>
                            <Label className="text-gray-700">GST Number</Label>
                            <Input 
                              name="gst_number"
                              value={formData.gst_number}
                              onChange={handleChange}
                              className="mt-1.5 h-11 border-gray-200 focus:border-emerald-500"
                              placeholder="Enter 15-digit GSTIN"
                            />
                          </div>
                        )}
                        <div className={formData.is_gst_registered ? "" : "col-span-2"}>
                          <Label className="text-gray-700">PAN Number</Label>
                          <Input 
                            name="pan_number"
                            value={formData.pan_number}
                            onChange={handleChange}
                            className="mt-1.5 h-11 border-gray-200 focus:border-emerald-500"
                            placeholder="Optional"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-gray-700">Office Address *</Label>
                        <Input 
                          name="address"
                          value={formData.address}
                          onChange={handleChange}
                          className="mt-1.5 h-11 border-gray-200 focus:border-emerald-500"
                          placeholder="Street address"
                          data-testid="register-address"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-gray-700">City *</Label>
                          <Input 
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            className="mt-1.5 h-11 border-gray-200 focus:border-emerald-500"
                            placeholder="City"
                            data-testid="register-city"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-700">State *</Label>
                          <Input 
                            name="state"
                            value={formData.state}
                            onChange={handleChange}
                            className="mt-1.5 h-11 border-gray-200 focus:border-emerald-500"
                            placeholder="State"
                            data-testid="register-state"
                          />
                        </div>
                      </div>

                      <div className="flex gap-3 mt-2">
                        <Button 
                          type="button" 
                          variant="outline"
                          onClick={() => setStep(1)}
                          className="flex-1 h-11 border-gray-200 rounded-xl"
                        >
                          <ArrowLeft className="w-4 h-4 mr-2" /> Back
                        </Button>
                        <Button 
                          type="button" 
                          onClick={nextStep}
                          className="flex-1 h-11 bg-emerald-600 hover:bg-emerald-700 rounded-xl"
                        >
                          Continue <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    </motion.div>
                  )}

                  {/* Step 3: Review */}
                  {step === 3 && (
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="space-y-5"
                    >
                      <div className="flex items-center gap-2 text-emerald-600 mb-4">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">Review & Submit</span>
                      </div>

                      <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-500 text-sm">Name</span>
                          <span className="font-medium text-gray-900">{formData.name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500 text-sm">Email</span>
                          <span className="font-medium text-gray-900">{formData.email}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500 text-sm">Phone</span>
                          <span className="font-medium text-gray-900">{countryCode} {formData.phone}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500 text-sm">Company</span>
                          <span className="font-medium text-gray-900">{formData.company_name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500 text-sm">Location</span>
                          <span className="font-medium text-gray-900">{formData.city}, {formData.state}</span>
                        </div>
                      </div>

                      <div className="p-4 bg-emerald-50 rounded-xl">
                        <div className="flex gap-3">
                          <Shield className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <h4 className="font-medium text-gray-900 text-sm">Secure OTP Login</h4>
                            <p className="text-xs text-gray-600 mt-1">
                              No password needed. Receive OTP via email each time you login.
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-3 mt-2">
                        <Button 
                          type="button" 
                          variant="outline"
                          onClick={() => setStep(2)}
                          className="flex-1 h-11 border-gray-200 rounded-xl"
                        >
                          <ArrowLeft className="w-4 h-4 mr-2" /> Back
                        </Button>
                        <Button 
                          type="submit" 
                          disabled={loading}
                          className="flex-1 h-11 bg-emerald-600 hover:bg-emerald-700 rounded-xl"
                          data-testid="register-submit"
                        >
                          {loading ? (
                            <span className="flex items-center gap-2">
                              <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                              Submitting...
                            </span>
                          ) : (
                            "Complete Registration"
                          )}
                        </Button>
                      </div>
                    </motion.div>
                  )}
                </form>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentRegisterPage;
